import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonService } from 'src/@services/common.service';
import { InnerPagesService } from 'src/app/pages/common-layout/inner-pages.service';
import { CommonPagesService } from '../../common-pages.service';
import { ResponseStatus, RoleType } from '../../enum';
import {
  displayPermissionSectionHeader,
  isCollapsiblePermissionSection,
  isSettingsSectionHeader,
} from './permission-header.util';
import {
  applyVisiblePermissionToggle,
  derivePermissionUiFlags,
  getModuleCapabilities,
  isInvestmentModule,
  isInvestorModule,
  isPaymentModule,
  isVendorModule,
  setAllVisiblePermissionFlags,
  syncHasAllFromCaps,
} from './permission-module-capabilities';

@Component({
  selector: 'app-add-update-permissions',
  templateUrl: './add-update-permissions.component.html',
  styleUrls: ['./add-update-permissions.component.scss'],

  standalone: false,
})
export class AddUpdatePermissionsComponent implements OnInit {
  isVisiblePermissionDrawer = true;
  permissionsByRole: any[] = [];
  @Input() selectedRoleData: any;
  @Input() currentSelectedTab: any;
  @Output() hidePermissionDrawer = new EventEmitter();
  rolePermissions: any;
  paybevRolePermissions: any;
  brooklynRolePermissions: any;
  supplierRolesPermissions: any;
  canEditRole: boolean = true;
  isLoadingOne = false;
  currentSelectPermissionsTab: number = 0;


  readonly paybevOnlyToggle = (
    moduleId: number,
    childIndex: number,
    trueValue: boolean,
    checkbox: 'view' | 'add' | 'edit' | 'delete',
    moduleType: string
  ) => this.UncheckAllCheck(moduleId, childIndex, trueValue, checkbox, moduleType);

  readonly paybevOnlyAll = (
    moduleId: number,
    childIndex: number,
    trueValue: boolean,
    moduleType: string
  ) => this.checkUncheckAll(moduleId, childIndex, trueValue, moduleType);

  readonly brooklynToggle = (
    moduleId: number,
    childIndex: number,
    trueValue: boolean,
    checkbox: 'view' | 'add' | 'edit' | 'delete',
    moduleType: string
  ) =>
    this.UncheckAllCheckBrooklynPermissions(
      moduleId,
      childIndex,
      trueValue,
      checkbox,
      moduleType
    );

  readonly brooklynAll = (
    moduleId: number,
    childIndex: number,
    trueValue: boolean,
    moduleType: string
  ) => this.checkUncheckAll(moduleId, childIndex, trueValue, moduleType);

  readonly paybevToggle = (
    moduleId: number,
    childIndex: number,
    trueValue: boolean,
    checkbox: 'view' | 'add' | 'edit' | 'delete',
    moduleType: string
  ) =>
    this.UncheckAllCheckPaybevPermissions(
      moduleId,
      childIndex,
      trueValue,
      checkbox,
      moduleType
    );

  readonly paybevAll = (
    moduleId: number,
    childIndex: number,
    trueValue: boolean,
    moduleType: string
  ) => this.checkUncheckAll(moduleId, childIndex, trueValue, moduleType);

  readonly supplierToggle = (
    moduleId: number,
    childIndex: number,
    trueValue: boolean,
    checkbox: 'view' | 'add' | 'edit' | 'delete',
    moduleType: string
  ) =>
    this.UncheckAllCheckSupplierPermissions(
      moduleId,
      childIndex,
      trueValue,
      checkbox,
      moduleType
    );

  readonly supplierAll = (
    moduleId: number,
    childIndex: number,
    trueValue: boolean,
    moduleType: string
  ) => this.checkUncheckAll(moduleId, childIndex, trueValue, moduleType);

  get RoleType() {
    return RoleType;
  }

  private findLeafByModule(groups: any[], moduleName: string): any | null {
    for (const group of groups ?? []) {
      const leaf = group?.permissions?.find((p: any) => p?.module === moduleName);
      if (leaf) {
        return leaf;
      }
    }
    return null;
  }

  private findLeafByModuleAnyName(
    groups: any[],
    matcher: (module: string) => boolean
  ): any | null {
    for (const group of groups ?? []) {
      const leaf = group?.permissions?.find((p: any) => matcher(p?.module ?? ''));
      if (leaf) {
        return leaf;
      }
    }
    return null;
  }

  private clearPermissionLeaf(leaf: any): void {
    if (!leaf) {
      return;
    }
    leaf.canView = false;
    leaf.canAdd = false;
    leaf.canEdit = false;
    leaf.canDelete = false;
    leaf.hasAll = false;
  }

  /**
   * Paybev Investments Add gates Investors; Expenses Add gates Vendors.
   * Only for Paybev-only roles (Brooklyn-active role editor keeps rows independent).
   */
  private applyInvestmentPaymentLinkFlags(groups: any[], moduleType: string): void {
    if (!groups?.length) {
      return;
    }
    const gateLinks =
      (moduleType === 'Paybev' || moduleType === 'Supplier') &&
      !this.selectedRoleData?.isBrooklynActive;
    const investmentLeaf = this.findLeafByModuleAnyName(groups, isInvestmentModule);
    const paymentLeaf = this.findLeafByModuleAnyName(groups, isPaymentModule);
    const investmentAdd = !!investmentLeaf?.canAdd;
    const paymentAdd = !!paymentLeaf?.canAdd;

    groups.forEach((group) => {
      group.permissions?.forEach((child: any) => {
        const module = child.module ?? '';
        if (!gateLinks) {
          child._uiDisabled = false;
          return;
        }
        if (isInvestorModule(module)) {
          child._uiDisabled = !investmentAdd;
        } else if (isVendorModule(module)) {
          child._uiDisabled = !paymentAdd;
        } else {
          child._uiDisabled = false;
        }
      });
    });
  }

  private cascadeInvestmentPaymentLinks(
    groups: any[],
    moduleType: string,
    sourceModule: string,
    addChecked: boolean
  ): void {
    if (!groups?.length || addChecked) {
      this.applyInvestmentPaymentLinkFlags(groups, moduleType);
      return;
    }
    if (isInvestmentModule(sourceModule)) {
      this.clearPermissionLeaf(
        this.findLeafByModuleAnyName(groups, isInvestorModule)
      );
    }
    if (isPaymentModule(sourceModule)) {
      this.clearPermissionLeaf(this.findLeafByModuleAnyName(groups, isVendorModule));
    }
    this.applyInvestmentPaymentLinkFlags(groups, moduleType);
  }

  private applyPermissionCheckboxVisibility(groups: any[] | null | undefined): void {
    if (!groups?.length) {
      return;
    }
    const moduleType = groups[0]?.moduleType ?? 'Brooklyn';

    groups.forEach((group) => {
      const groupModuleType = group?.moduleType ?? moduleType;
      group._uiCollapsible = isCollapsiblePermissionSection(group?.header);
      group._uiHeaderLabel = displayPermissionSectionHeader(group?.header);
      // Rows in a headed group read as children of that header; rows in a headerless
      // group are top-level menu items and are styled like a header themselves.
      group._uiNested = !!group?.header;
      if (group._uiExpanded === undefined) {
        group._uiExpanded = true;
      }
      group.permissions?.forEach((child: any) => {
        const module = child.module ?? '';
        const caps = getModuleCapabilities(groupModuleType, module);
        const uiFlags = derivePermissionUiFlags(caps);
        child._uiShowAll = uiFlags._uiShowAll;
        child._uiShowView = uiFlags._uiShowView;
        child._uiShowAdd = uiFlags._uiShowAdd;
        child._uiShowEdit = uiFlags._uiShowEdit;
        child._uiShowDelete = uiFlags._uiShowDelete;
        if (child._uiDisabled === undefined) {
          child._uiDisabled = false;
        }
      });
    });

    this.applyInvestmentPaymentLinkFlags(groups, moduleType);
  }


  private initializeHasAllFlags(groups: any[] | null | undefined): void {
    if (!groups?.length) {
      return;
    }
    const moduleType = groups[0]?.moduleType ?? 'Paybev';
    groups.forEach((group) => {
      const groupModuleType = group?.moduleType ?? moduleType;
      group.permissions?.forEach((child: any) => {
        syncHasAllFromCaps(child, groupModuleType, child.module ?? '');
      });
    });
  }

  constructor(
    public _cs: CommonService,
    private _ws: CommonPagesService,
    private _iS: InnerPagesService
  ) {}

  ngOnInit(): void {
    if (
      !this.selectedRoleData.isBrooklynActive &&
      this.currentSelectedTab != RoleType.Supplier
    ) {
      this.permissionsByRole = JSON.parse(
        JSON.stringify(this.selectedRoleData['permissions'])
      );
    } else {
      const brooklynRolePermissions = this.selectedRoleData?.permissions.filter(
        (x: { moduleType: string }) => x.moduleType === 'Brooklyn'
      );

      const paybevRolePermissions = this.selectedRoleData?.permissions.filter(
        (x: { moduleType: string }) => x.moduleType === 'Paybev'
      );

      const supplierRolesPermissions = this.selectedRoleData?.permissions.filter(
        (x: { moduleType: string }) => x.moduleType === 'Supplier'
      );

      this.brooklynRolePermissions = JSON.parse(
        JSON.stringify(brooklynRolePermissions)
      );
      this.paybevRolePermissions = JSON.parse(
        JSON.stringify(paybevRolePermissions)
      );
      this.supplierRolesPermissions = JSON.parse(
        JSON.stringify(supplierRolesPermissions)
      );
    }

    this.canEditRole = this._iS.canAccessRoles().canEditRole;
    !this.selectedRoleData.isBrooklynActive &&
    this.currentSelectedTab != RoleType.Supplier
      ? this.getPermissionsByRole()
      : this.getBrooklynPaybevSupplierPermissionsByRole();
  }

  onTabChange(tabIndex: number) {
    this.currentSelectPermissionsTab = tabIndex;
  }

  getPermissionsByRole() {
    this.initializeHasAllFlags(this.permissionsByRole);
    this.applyPermissionCheckboxVisibility(this.permissionsByRole);
  }

  getBrooklynPaybevSupplierPermissionsByRole() {
    this.initializeHasAllFlags(this.brooklynRolePermissions);
    this.initializeHasAllFlags(this.paybevRolePermissions);
    this.initializeHasAllFlags(this.supplierRolesPermissions);

    this.applyPermissionCheckboxVisibility(this.brooklynRolePermissions);
    this.applyPermissionCheckboxVisibility(this.paybevRolePermissions);
    this.applyPermissionCheckboxVisibility(this.supplierRolesPermissions);
  }

  checkUncheckAll(
    moduleId: number,
    childIndex: number,
    trueValue: boolean,
    moduleType: string
  ) {
    const { groups, group } = this.resolvePermissionGroup(moduleId);
    if (!group?.permissions?.[childIndex]) {
      return;
    }
    const child = group.permissions[childIndex];
    setAllVisiblePermissionFlags(child, moduleType, child.module ?? '', trueValue);
    this.syncInvestmentPaymentLinksAfterChange(
      groups,
      moduleType,
      child.module
    );
  }

  private resolvePermissionGroup(moduleId: number): { groups: any[]; group: any } {
    if (!this.selectedRoleData.isBrooklynActive) {
      if (this.currentSelectedTab == RoleType.Supplier) {
        if (this.currentSelectPermissionsTab == 0) {
          return {
            groups: this.supplierRolesPermissions,
            group: this.supplierRolesPermissions[moduleId],
          };
        }
        return {
          groups: this.paybevRolePermissions,
          group: this.paybevRolePermissions[moduleId],
        };
      }
      return {
        groups: this.permissionsByRole,
        group: this.permissionsByRole[moduleId],
      };
    }

    if (this.currentSelectPermissionsTab == 0) {
      return {
        groups: this.brooklynRolePermissions,
        group: this.brooklynRolePermissions[moduleId],
      };
    }
    if (this.currentSelectPermissionsTab == 1) {
      return {
        groups: this.paybevRolePermissions,
        group: this.paybevRolePermissions[moduleId],
      };
    }
    return {
      groups: this.supplierRolesPermissions,
      group: this.supplierRolesPermissions[moduleId],
    };
  }

  private syncInvestmentPaymentLinksAfterChange(
    groups: any[],
    moduleType: string,
    changedModule: string | undefined
  ): void {
    if (!changedModule) {
      return;
    }
    if (isInvestmentModule(changedModule) || isPaymentModule(changedModule)) {
      const source = this.findLeafByModule(groups, changedModule);
      this.cascadeInvestmentPaymentLinks(
        groups,
        moduleType,
        changedModule,
        !!source?.canAdd
      );
    }
  }

  UncheckAllCheckSupplierPermissions(
    moduleId: number,
    childIndex: number,
    trueValue: boolean,
    checkbox: 'view' | 'add' | 'edit' | 'delete',
    moduleType: string
  ) {
    const group = this.supplierRolesPermissions[moduleId];
    const child = group?.permissions?.[childIndex];
    if (!child) {
      return;
    }
    applyVisiblePermissionToggle(
      child,
      moduleType,
      child.module ?? '',
      checkbox,
      trueValue
    );
    this.applyLegacySupplierAdminCascade(moduleId, childIndex, checkbox, trueValue);
    this.syncInvestmentPaymentLinksAfterChange(
      this.supplierRolesPermissions,
      moduleType,
      child.module
    );
  }

  /** Preserves legacy Supplier Admin-section index coupling (out of capability-matrix scope). */
  private applyLegacySupplierAdminCascade(
    moduleId: number,
    childIndex: number,
    checkbox: string,
    trueValue: boolean
  ): void {
    if (trueValue || checkbox !== 'add') {
      return;
    }
    if (moduleId === 2 && this.supplierRolesPermissions[2]?.permissions?.[0]) {
      this.clearPermissionLeaf(this.supplierRolesPermissions[2].permissions[0]);
    }
    if (moduleId === 3 && this.supplierRolesPermissions[3]?.permissions?.[0]) {
      this.clearPermissionLeaf(this.supplierRolesPermissions[3].permissions[0]);
    }
  }

  UncheckAllCheckBrooklynPermissions(
    moduleId: number,
    childIndex: number,
    trueValue: boolean,
    checkbox: 'view' | 'add' | 'edit' | 'delete',
    moduleType: string
  ) {
    const child = this.brooklynRolePermissions[moduleId]?.permissions?.[childIndex];
    if (!child) {
      return;
    }
    applyVisiblePermissionToggle(
      child,
      moduleType,
      child.module ?? '',
      checkbox,
      trueValue
    );
    this.syncInvestmentPaymentLinksAfterChange(
      this.brooklynRolePermissions,
      moduleType,
      child.module
    );
  }

  UncheckAllCheckPaybevPermissions(
    moduleId: number,
    childIndex: number,
    trueValue: boolean,
    checkbox: 'view' | 'add' | 'edit' | 'delete',
    moduleType: string
  ) {
    const child = this.paybevRolePermissions[moduleId]?.permissions?.[childIndex];
    if (!child) {
      return;
    }
    applyVisiblePermissionToggle(
      child,
      moduleType,
      child.module ?? '',
      checkbox,
      trueValue
    );
    this.syncInvestmentPaymentLinksAfterChange(
      this.paybevRolePermissions,
      moduleType,
      child.module
    );
  }

  UncheckAllCheck(
    moduleId: number,
    childIndex: number,
    trueValue: boolean,
    checkbox: 'view' | 'add' | 'edit' | 'delete',
    moduleType: string
  ) {
    const child = this.permissionsByRole[moduleId]?.permissions?.[childIndex];
    if (!child) {
      return;
    }
    applyVisiblePermissionToggle(
      child,
      moduleType,
      child.module ?? '',
      checkbox,
      trueValue
    );
    this.syncInvestmentPaymentLinksAfterChange(
      this.permissionsByRole,
      moduleType,
      child.module
    );
  }

  cancelPermissionDrawer() {
    this.isVisiblePermissionDrawer = false;
    this.hidePermissionDrawer.emit({
      hide: this.isVisiblePermissionDrawer,
      data: null,
    });
  }

  handleOkPermissionDrawer() {
    let permissions: any[] = [];
    if (
      !this.selectedRoleData.isBrooklynActive &&
      this.currentSelectedTab != RoleType.Supplier
    ) {
      permissions = this.permissionsByRole;
    } else {
      const brooklynPermissions = this.brooklynRolePermissions;
      const paybevPermissions = this.paybevRolePermissions;
      const supplierPermissions: any = [...this.supplierRolesPermissions];

      permissions =
        this.currentSelectedTab == RoleType.Supplier
          ? [...paybevPermissions, ...supplierPermissions]
          : [
              ...brooklynPermissions,
              ...paybevPermissions,
              ...supplierPermissions,
            ];
    }

    this.isLoadingOne = true;
    const permissionBody = {
      Id: this.selectedRoleData.id,
      Permissions: permissions,
    };

    this._ws
      .updatePermissionsByRole(permissionBody)
      .subscribe((response: any) => {
        if (response.responseStatus == ResponseStatus.success) {
          this.isVisiblePermissionDrawer = false;
          this.hidePermissionDrawer.emit({
            hide: this.isVisiblePermissionDrawer,
            data: response.result,
          });
          this._cs.displayMessage('success', response.message);
          this.isLoadingOne = false;
        } else {
          this._cs.displayMessage('error', response.message);
          this.isLoadingOne = false;
        }
      });
  }
}
